import type { TextFilterOperator } from '@/components/filters/filter-menu'
import type { UnifiedFilterOperator } from '@/components/filters/list-filter-types'
import { truncateChipDisplay } from '@/components/filters/filter-styles'

export function parseCommaList(value: string | null, allowed?: Set<string>): string[] {
  if (!value?.trim()) return []
  const values = value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
  if (!allowed) return values
  return values.filter((entry) => allowed.has(entry))
}

export function serializeCommaList(values: string[]): string {
  return values.join(',')
}

export function parseTextFilterOperator(value: string | null): TextFilterOperator {
  if (value === 'is' || value === 'is_not' || value === 'does_not_contain') return value
  return 'contains'
}

export function parseUnifiedFilterOperator(value: string | null): UnifiedFilterOperator {
  return value === 'is_not' ? 'is_not' : 'is'
}

export function formatUnifiedFilterOperatorChip(operator: UnifiedFilterOperator): string {
  return operator === 'is_not' ? 'is not' : 'is'
}

export function formatUnifiedFilterOperatorPicker(operator: UnifiedFilterOperator): string {
  return operator === 'is_not' ? 'Is not' : 'Is'
}

export function formatTextFilterOperatorChip(
  operator: Extract<TextFilterOperator, 'contains' | 'does_not_contain'>,
): string {
  return operator === 'does_not_contain' ? 'do not contain' : 'contain'
}

export function parseCommaKeywordOperator(value: string | null): 'contains' | 'does_not_contain' {
  return value === 'does_not_contain' ? 'does_not_contain' : 'contains'
}

export function normalizeCommaSeparatedTextQuery(raw: string): string {
  return parseCommaList(raw).join(', ')
}

/** Lowercase terms from a comma-separated text-contains chip value (OR match). */
export function textContainsSearchTerms(query: string): string[] {
  return normalizeCommaSeparatedTextQuery(query)
    .split(',')
    .map((term) => term.trim().toLowerCase())
    .filter(Boolean)
}

/** True when any term is a case-insensitive substring of any haystack (comma terms = OR). */
export function matchesTextContainsHaystacks(haystacks: string[], query: string): boolean {
  const terms = textContainsSearchTerms(query)
  if (terms.length === 0) return true
  const normalized = haystacks.map((value) => value.toLowerCase())
  return terms.some((term) => normalized.some((value) => value.includes(term)))
}

export function formatCommaKeywordCommitHint(query: string): string {
  const trimmed = query.trim()
  if (!trimmed) return ''
  const terms = parseCommaList(trimmed)
  if (terms.length <= 1) {
    return `Press Enter to search “${trimmed}”`
  }
  const joined = terms.map((term) => `“${term}”`).join(' or ')
  return `Press Enter to search for ${joined} (any match)`
}

export function formatCommaKeywordDisplay(q: string): string {
  const terms = parseCommaList(q)
  if (terms.length === 0) return ''
  return formatFreeTextChipDisplay(terms.join(', '))
}

/** Multi-value chips show names for 1–2 picks; count summary from this threshold up. */
export const MULTI_VALUE_CHIP_COUNT_FROM = 3

/** Free-text filter chips (keyword, location, organisation) — truncate, never “N selected”. */
export function formatFreeTextChipDisplay(value: string): string {
  const trimmed = value.trim()
  if (!trimmed) return 'Any'
  return truncateChipDisplay(trimmed)
}

export function formatMultiValueChipDisplayFromLabels(
  labels: string[],
  options: { emptyLabel?: string } = {},
): string {
  const emptyLabel = options.emptyLabel ?? 'Any'
  if (labels.length === 0) return emptyLabel
  if (labels.length === 1) return labels[0]
  if (labels.length === 2) return labels.join(', ')
  return `${labels.length} selected`
}

export function matchesCommaKeywordFilter(
  haystack: string,
  terms: string[],
  operator: 'contains' | 'does_not_contain',
): boolean {
  if (terms.length === 0) return true
  const normalized = haystack.toLowerCase()
  const anyTermMatches = terms.some((term) => normalized.includes(term.toLowerCase()))
  return operator === 'does_not_contain' ? !anyTermMatches : anyTermMatches
}

export function nextChipOperatorLabel(operator: string): string {
  if (operator === 'contain') return 'do not contain'
  if (operator === 'do not contain') return 'contain'
  if (operator === 'is') return 'is not'
  return 'is'
}

export function formatMultiValueChipDisplay(
  values: string[],
  labels: Record<string, string>,
  options: { emptyLabel?: string } = {},
): string {
  const emptyLabel = options.emptyLabel ?? 'Any'
  if (values.length === 0) return emptyLabel
  const displayLabels = values.map((value) => labels[value] ?? value)
  return formatMultiValueChipDisplayFromLabels(displayLabels, { emptyLabel })
}

export function matchesUnifiedListFilter(
  selected: string[],
  actual: string,
  operator: UnifiedFilterOperator = 'is',
): boolean {
  if (selected.length === 0) return true
  const inList = selected.includes(actual)
  return operator === 'is_not' ? !inList : inList
}
